import { $, html, rune, type Html, View } from 'rune-ts';
import klass from './InterviewSection.module.scss';
import { staticTypoPcMo, typoPcMo } from '../../../../../shared/typography/typo';
import { ArrowRightIcon } from '../../../../components/atoms/Icon';
import Swiper from 'swiper';
import { EffectFade, Navigation } from 'swiper/modules';
import { map, pipe, range, toArray } from '@fxts/core';
import { InterviewCard } from './InterviewCard';
import { getResizedUrl } from '../../../../../shared/util/image';

export class InterviewSection extends View {
  swiper?: Swiper;
  current_index = 1;
  max_index = 3;

  constructor() {
    super({});
  }

  override template(): Html {
    const is_mobile = !!rune.getSharedData(this)?.is_mobile;
    const texts = {
      title: html.preventEscape(ET(`mps2::landing::handmade::interview_title`)),
      sub_title: html.preventEscape(ET(`mps2::landing::handmade::interview_sub_title`)),
      description: html.preventEscape(ET(`mps2::landing::handmade::interview_description`)),
    };

    return html`
      <section class="${klass.interview}">
        <div class="${klass.introduce}">
          <div class="${klass.content}">
            <div class="${typoPcMo({ is_mobile, pc: '48_bold', mo: '24_bold' })}">${texts.title}</div>
            <div class="${typoPcMo({ is_mobile, pc: '16_medium', mo: '14_medium' })}">${texts.sub_title}</div>
            <div class="${typoPcMo({ is_mobile, pc: '16_medium', mo: '14_medium' })}">
              ${texts.description}
            </div>
            <a href="https://marpple.shop/" target="_blank">
              <div class="${klass.link} ${typoPcMo({ is_mobile, pc: '16_bold', mo: '14_bold' })}">
                Make it Real, MarppleShop <span class="${klass.arrow_icon}">${ArrowRightIcon}</span>
              </div>
            </a>
          </div>
          ${is_mobile
            ? html`
                <div class="${klass.video_wrapper}">
                  <video
                    preload="none"
                    autoplay=""
                    loop=""
                    muted=""
                    playsinline=""
                    class="${klass.motion_video}"
                    src="//s3.ap-northeast-2.amazonaws.com/s3.marpple.co/files/u_4648126/2024/10/original/wax_stamp.mp4"
                  ></video>
                </div>
              `
            : html`
                <div class="${klass.video_wrapper}">
                  <img
                    class="${klass.motion_video}"
                    src="${getResizedUrl({
                      url: '//s3.marpple.co/files/u_4648126/2024/11/original/7de3e6023499aef952ecbe26f29dd1ceb16617be1.png',
                      format: 'webp',
                      width: 588 * 2,
                      quality: 85,
                    })}"
                    alt="interveiw section main image"
                  />
                </div>
              `}
        </div>

        ${is_mobile
          ? html`
              <div class="${klass.slide}">
                <div class="${klass.swiper}">
                  <div class="${klass.swiper_wrapper} swiper-wrapper">${this._renderCardList()}</div>
                </div>
                <div
                  class="pagination ${klass.pagination} ${staticTypoPcMo({
                    is_mobile,
                    pc: 'bebas_16_bold',
                    mo: 'bebas_14_bold',
                  })}"
                >
                  <span class="current_index"> 0${this.current_index} </span>
                  <div class="${klass.pages}">
                    <div class="progress ${klass.page}" style="width: ${this.calcProgress()}%;"></div>
                  </div>
                  <span> 0${this.max_index} </span>
                </div>
              </div>
            `
          : html` <div class="${klass.creator_list}">${this._renderCardList()}</div> `}
      </section>
    `;
  }

  override onRender() {
    const is_mobile = !!rune.getSharedData(this)?.is_mobile;
    if (is_mobile) {
      this.swiper = this._onLoadSwiper();
    }
  }

  private _onLoadSwiper() {
    return new Swiper(`.${klass.swiper}`, this._createSwiperOption());
  }

  private _createSwiperOption() {
    return {
      modules: [Navigation, EffectFade],
      slidesPerView: 'auto' as const,
      spaceBetween: 24,
      slidesOffsetBefore: 24,
      slidesOffsetAfter: 48,
      on: {
        slideChange: (swiper: Swiper) => {
          this.setCurrentIndex(swiper.realIndex + 1);
          this.redraw();
        },
      },
    };
  }

  private _renderCardList() {
    return pipe(
      range(1, 4),
      map((card_number) => new InterviewCard({ card_number })),
      toArray,
    );
  }

  calcProgress() {
    return (this.current_index / this.max_index) * 100;
  }

  setCurrentIndex(index: number) {
    this.current_index = index;
  }

  override redraw(): this {
    $(this.element()).find('.current_index')?.setInnerHtml(`0${this.current_index.toString()}`);
    const progress = $(this.element()).find('.progress')?.element();
    if (progress) {
      progress.style.width = `${this.calcProgress()}%`;
    }

    return this;
  }
}
